// src/pages/MyQuotes.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, Chip,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { quotesAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import AppHeader from '../components/AppHeader';
import { Quote } from '../types';
import { formatCurrency } from '../utils/formatCurrency';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const MyQuotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchQuotes(); }, []);

  const fetchQuotes = async () => {
    try {
      const response = await quotesAPI.getAll();
      setQuotes(response.data);
      setError('');
    } catch {
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try {
      await quotesAPI.delete(id);
      setSuccess('Quote deleted successfully');
      fetchQuotes();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to delete quote');
    }
  };

  const handleLoad = (quoteId: number) => {
    navigate(`/quote-builder?load=${quoteId}`);
  };

  const handleExportPDF = async (quoteId: number) => {
    try {
      const response = await quotesAPI.getById(quoteId);
      const { quote } = response.data;

      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text('Cost Calculator Quote', 14, 20);

      doc.setFontSize(10);
      doc.text(`Quote: ${quote.QuoteName ?? 'Untitled'}`, 14, 30);
      doc.text(`Provider: ${quote.ProviderName}`, 14, 36);
      doc.text(`Region: ${quote.RegionName}`, 14, 42);
      doc.text(`Date: ${new Date(quote.CreatedAt).toLocaleDateString()}`, 14, 48);

      doc.save(`quote-${quoteId}.pdf`);
      setSuccess('PDF exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to export PDF');
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <>
      <AppHeader />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>Saved Quotes</Typography>
          <Typography variant="body2" color="text.secondary">Manage your saved infrastructure quotes</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {quotes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>No Saved Quotes</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Start building your first quote to see it here
            </Typography>
            <Button variant="contained" color="secondary" onClick={() => navigate('/quote-builder')}>
              Create New Quote
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Quote Name</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Monthly Cost</TableCell>
                  <TableCell align="right">Annual Cost</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map((quote) => (
                  <TableRow key={quote.QuoteId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {quote.QuoteName ?? 'Untitled Quote'}
                      </Typography>
                    </TableCell>
                    <TableCell><Chip label={quote.ProviderName} size="small" /></TableCell>
                    <TableCell>{quote.RegionName}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(Number(quote.TotalMonthlyCost ?? 0))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(Number(quote.TotalAnnualCost ?? 0))}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(quote.CreatedAt).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(quote.UpdatedAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleLoad(quote.QuoteId)} title="Load quote">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleExportPDF(quote.QuoteId)} title="Export to PDF">
                        <PdfIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(quote.QuoteId)} title="Delete quote">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Quote"
        message="Are you sure you want to delete this quote?"
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </>
  );
};

export default MyQuotes;
